/* lactation-promote-lottery.js
 * Lactation Promote Lottery — 抽奖组件 JS
 * 复用 m26-mystery-box.js / 26-pd-promotion-lottery.js 中的通用基础设施
 * （M26Modal / M26Lottery / m26-login-guard 等），本文件只实现：
 *   - 产品气泡随机布局（环绕中间大气泡，无重叠）
 *   - 初始化气泡律动动画
 *   - 点击抽奖 → 登录校验 → 次数检查 → 调用 API（携带购物车数据）
 *   - 抽奖中气泡混乱滚动 + 中间气泡消失
 *   - 中奖结果展示（产品图、折扣、折扣码、Copy/Copied 复制）
 *   - Rules / Prize Records 弹框（Tab 切换，与 26-pd-promotion-lottery 一致）
 */

(async function () {
  "use strict";

  /* ── 工具：从 cookie 读取 JWT ────────────────────────────────────────── */
  function _getCookie(name) {
    const cookies = document.cookie.split("; ");
    const values = [];
    for (const cookie of cookies) {
      const [key, value] = cookie.split("=");
      if (key === name) {
        values.push(decodeURIComponent(value));
      }
    }
    // 返回数组中的最后一个元素
    return values.length > 0 ? values[values.length - 1] : null;
  }

  function _parseJwt(token) {
    try {
      const payload = token.split(".")[1];
      if (!payload) return null;
      const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
      const pad = "=".repeat((4 - (base64.length % 4)) % 4);
      return JSON.parse(atob(base64 + pad));
    } catch (_) {
      return null;
    }
  }

  /* ── 解析 API 前缀
   * URL 含 mode=dev 时使用开发环境，否则使用正式环境。
   * 优先读取 window.__MC__CONFIG__.apiUrl（全局覆盖），其次按 mode 参数自动判断。
   * ─────────────────────────────────────────────────────────────────────── */
  function _resolveApiBase() {
    if (window.__MC__CONFIG__ && window.__MC__CONFIG__.apiUrl) {
      return window.__MC__CONFIG__.apiUrl;
    }
    const params = new URLSearchParams(window.location.search);
    return params.get("mode") === "dev"
      ? "https://station-api-dev.luteos.com"
      : "https://account.momcozy.com";
  }

  /* ── 登录唤起（专用于本组件，确保 redirect 回当前页且不带 #mystery-box hash）
   * m26Login / 全局 _login 的 redirect 会被 getRedirectUrl() 强制加上 #mystery-box，
   * 导致页面刷新后浏览器锚点跳转覆盖本组件的 scrollIntoView。
   * 此函数直接调用 login-dialog.open()，传入去掉 hash 的干净 URL 作为 redirect，
   * 保证刷新后不被锚点滚动干扰。
   * ─────────────────────────────────────────────────────────────────────── */
  function _openLoginForLPL() {
    const loginDialog = document.querySelector("login-dialog");
    if (!loginDialog || typeof loginDialog.open !== "function") {
      /* 兜底：用全局 _login */
      if (_login) _login();
      return;
    }
    /* 去掉 hash，只保留 path + query，避免 #mystery-box 锚点覆盖滚动 */
    const cleanUrl = window.location.href.split("#")[0];
    loginDialog.open(null, {
      activityType:
        window.__MC_ACTIVITY_SOURCE__ ||
        "activity_" + (window.__MC_ACTIVITY_TYPE__ || ""),
      mustAgreeEmail: true,
      redirect: cleanUrl,
    });
  }

  /* ── 复用 m26 基础设施（若已加载则直接引用，否则创建轻量回退） ──────── */
  let _Modal = null;
  let _auth = null;
  let _fetch = null;
  let _login = null;
  let _collectionProductMap = null;

  function _ensureBase() {
    if (_Modal && _auth && _fetch) return;

    /* 优先使用已有全局 */
    if (typeof M26Modal !== "undefined") _Modal = M26Modal;
    if (typeof m26Auth !== "undefined") _auth = m26Auth;
    if (typeof m26Fetch !== "undefined") _fetch = m26Fetch;
    if (typeof m26Login !== "undefined") _login = m26Login;
    if (_Modal && _auth && _fetch) return;

    /* 轻量回退 auth */
    if (!_auth) {
      _auth = { user: null, _cbs: [] };
      _auth.onLogin = function (cb) {
        if (_auth.user) {
          cb(_auth.user);
          return;
        }
        _auth._cbs.push(cb);
      };
      const cookieName =
        (window.__MC__CONFIG__ && window.__MC__CONFIG__.cookieName) ||
        "momcozy-token";
      const token = _getCookie(cookieName);
      if (token) {
        const payload = _parseJwt(token) || {};
        _auth.user = {
          type: "cookie",
          token,
          email: payload.email || payload.sub || "",
        };
      }
      document.addEventListener("LUTE:AUTH:LOGIN", function (e) {
        _auth.user = e.detail;
        _auth._cbs.forEach(function (fn) {
          fn(_auth.user);
        });
        _auth._cbs.length = 0;
      });
      document.addEventListener("LUTE:AUTH:LOGOUT", function () {
        _auth.user = null;
      });
    }

    /* 轻量回退 fetch */
    if (!_fetch) {
      const apiBase = _resolveApiBase();
      _fetch = async function (path, opts) {
        opts = opts || {};
        const headers = {
          "Content-Type": "application/json",
          ...(window.__MC__CONFIG__ && window.__MC__CONFIG__.headers
            ? window.__MC__CONFIG__.headers
            : {}),
        };
        if (_auth.user && _auth.user.token)
          headers["Authorization"] = "Bearer " + _auth.user.token;
        const res = await fetch(
          apiBase + path,
          Object.assign({ headers }, opts),
        );
        const data = await res.json().catch(function () {
          return {};
        });
        if (data && data.code === "10001003") {
          _auth.user = null;
          document.dispatchEvent(new CustomEvent("lute:account:logout"));
          throw data;
        }
        if (data && data.code === "200")
          return data.result || data.data || true;
        throw data;
      };
    }

    /* 轻量回退 login */
    if (!_login) {
      _login = function () {
        const loginDialog = document.querySelector("login-dialog");
        if (loginDialog && typeof loginDialog.open === "function") {
          loginDialog.open(null, {
            activityType:
              window.__MC_ACTIVITY_SOURCE__ ||
              "activity_" + (window.__MC_ACTIVITY_TYPE__ || ""),
            mustAgreeEmail: true,
            redirect: window.location.href,
          });
        }
      };
    }

    /* 轻量回退 Modal（仅当 m26-mystery-box.js 未加载时） */
    if (!_Modal) {
      _Modal = class _FallbackModal {
        static _openCount = 0;
        static _overlayEl = null;
        static _getOverlay() {
          if (_FallbackModal._overlayEl) return _FallbackModal._overlayEl;
          const el = document.createElement("div");
          el.className = "m26-modal-overlay";
          el.addEventListener("click", function (e) {
            if (e.target !== el) return;
            for (let i = el.children.length - 1; i >= 0; i--) {
              const inst = el.children[i]._lpl;
              if (inst && inst._isOpen) {
                inst.close();
                break;
              }
            }
          });
          document.body.appendChild(el);
          _FallbackModal._overlayEl = el;
          return el;
        }
        constructor(className) {
          this._className = className || "";
          this._boxEl = null;
          this._bodyEl = null;
          this._bodyHtml = "";
          this._isOpen = false;
          this._triggerEl = null;
          this._closeTimer = null;
          this._handleEvent = this._handleEvent.bind(this);
        }
        setContent(html) {
          this._bodyHtml = html;
          if (this._bodyEl) this._bodyEl.innerHTML = html;
        }
        show() {
          this._triggerEl = document.activeElement;
          this._ensureBox();
          const box = this._boxEl;
          box.className =
            "m26-modal-box nt" + (this._className ? " " + this._className : "");
          if (this._bodyHtml && this._bodyEl)
            this._bodyEl.innerHTML = this._bodyHtml;
          const overlay = _FallbackModal._getOverlay();
          requestAnimationFrame(() => {
            overlay.classList.add("is-open");
            box.classList.add("is-open");
            _FallbackModal._openCount++;
            if (_FallbackModal._openCount === 1)
              document.documentElement.classList.add("m26-scroll-lock");
            document.addEventListener("keydown", this._handleEvent);
            this._isOpen = true;
            const first = box.querySelector(
              'button, [href], input, [tabindex]:not([tabindex="-1"])',
            );
            if (first) first.focus();
          });
        }
        close() {
          if (!this._boxEl || !this._isOpen) return;
          this._isOpen = false;
          this._boxEl.classList.remove("is-open");
          document.removeEventListener("keydown", this._handleEvent);
          this._closeTimer = setTimeout(() => this._afterClose(), 350);
          this._boxEl.addEventListener(
            "transitionend",
            (e) => {
              if (e.target === this._boxEl && !this._isOpen) this._afterClose();
            },
            { once: true },
          );
        }
        _afterClose() {
          clearTimeout(this._closeTimer);
          this._closeTimer = null;
          _FallbackModal._openCount = Math.max(
            0,
            _FallbackModal._openCount - 1,
          );
          if (_FallbackModal._openCount === 0) {
            document.documentElement.classList.remove("m26-scroll-lock");
            if (_FallbackModal._overlayEl)
              _FallbackModal._overlayEl.classList.remove("is-open");
          }
        }
        _handleEvent(e) {
          if (e.key === "Escape") this.close();
        }
        _ensureBox() {
          if (this._boxEl) return;
          const overlay = _FallbackModal._getOverlay();
          const box = document.createElement("div");
          box.className = "m26-modal-box nt";
          box.setAttribute("role", "dialog");
          box.setAttribute("aria-modal", "true");
          box.innerHTML = `
            <button type="button" class="m26-modal__close" data-close aria-label="Close">
              <svg class="m26-modal__close-icon" aria-hidden="true"><use xlink:href="#lactation-lottery-modal-close"></use></svg>
            </button>
            <div class="m26-modal__body"></div>`;
          box._lpl = this;
          box
            .querySelector("[data-close]")
            .addEventListener("click", () => this.close());
          overlay.appendChild(box);
          this._boxEl = box;
          this._bodyEl = box.querySelector(".m26-modal__body");
        }
        destroy() {
          document.removeEventListener("keydown", this._handleEvent);
          clearTimeout(this._closeTimer);
          if (this._isOpen) {
            _FallbackModal._openCount = Math.max(
              0,
              _FallbackModal._openCount - 1,
            );
            if (_FallbackModal._openCount === 0) {
              document.documentElement.classList.remove("m26-scroll-lock");
              if (_FallbackModal._overlayEl)
                _FallbackModal._overlayEl.classList.remove("is-open");
            }
          }
          if (this._boxEl) {
            this._boxEl.remove();
            this._boxEl = null;
            this._bodyEl = null;
          }
        }
      };
    }
  }

  /* ── 读取 cookie 同步 auth（每次操作前校验） ───────────────────────── */
  function _syncAuth() {
    const cookieName =
      (window.__MC__CONFIG__ && window.__MC__CONFIG__.cookieName) ||
      "momcozy-token";
    const token = _getCookie(cookieName);
    if (token && _auth) {
      const payload = _parseJwt(token) || {};
      _auth.user = {
        type: "cookie",
        token,
        email: payload.email || payload.sub || "",
      };
    } else if (_auth) {
      _auth.user = null;
    }
  }

  /* ── 获取购物车数据（提交给抽奖 API）──────────────────────────────── */
  async function _getCartItems() {
    try {
      const res = await fetch("/cart.js", {
        headers: { "Content-Type": "application/json" },
      });
      const cart = await res.json();
      if (!cart || !cart.items) return [];
      return cart.items.map(function (item) {
        return {
          productId: String(item.product_id),
          varityId: String(item.variant_id),
        };
      });
    } catch (_) {
      return [];
    }
  }

  /* ── 气泡布局算法 ───────────────────────────────────────────────────
   * PC 确定性布局（10个，左5右5，严格关于中心大气泡左右对称）：
   *
   * 右侧5个（从右侧确定，左侧直接 x 镜像，y / r 完全相同）：
   *   R0 右上内环  -45°  dist = cr+r+gap    大尺寸 sizeL
   *   R1 正右内环    0°  dist = cr+r+gap    中尺寸 sizeM
   *   R2 右下内环  +45°  dist = cr+r+gap    大尺寸 sizeL（与R0同dist → 水平对齐）
   *   R3 右外上   -20°  dist = outerDist   中小尺寸 sizeS
   *   R4 右外下   +20°  dist = outerDist   小尺寸 sizeXS（< sizeM，与R3同dist → 水平对齐）
   *
   * 左侧：x_L = stageW - x_R，y_L = y_R，r_L = r_R（完全镜像）
   *
   * footerH: PC 端 footer 占用的底部高度，所有气泡底部不得超过 stageH - footerH。
   *
   * Mobile: 7个气泡均匀环绕，不变。
   * ─────────────────────────────────────────────────────────────────── */

  /**
   * @param {number} stageW         舞台宽
   * @param {number} stageH         舞台高
   * @param {number} cx             中心 x
   * @param {number} cy             中心 y
   * @param {number} cr             中心圆排斥半径（第5圈半径）
   * @param {number} n              气泡总数（PC 固定传 10，Mobile 传 7）
   * @param {number} minR           气泡最小半径
   * @param {number} maxR           气泡最大半径
   * @param {boolean} isMobile
   * @param {number} footerH        PC footer 高度（底部安全预留）
   * @param {Array|null} pcSlotRadii PC 右侧5个槽的目标半径 [R0,R1,R2,R3,R4]（Mobile 传 null）
   * @returns {Array<{x,y,r}>}
   */
  function _layoutBubbles(
    stageW,
    stageH,
    cx,
    cy,
    cr,
    n,
    minR,
    maxR,
    isMobile,
    footerH,
    pcSlotRadii,
  ) {
    var padding = 4;
    var gap = 10;

    /* PC 端底部安全边界 */
    var safeBottom = stageH - (footerH || 0) - padding;

    /* ── Mobile 确定性布局 ── */
    if (isMobile) {
      /*
       * 正七边形环绕布局：7个气泡顶点角度均匀分布（间隔 360/7 ≈ 51.43°），
       * 从顶部（-90°）开始顺时针排列，各气泡独立目标直径，dist = cr + r + gap。
       *
       * 角度顺序（从 -90° 顺时针，步进 51.43°）：
       *   M0 左上    -141.4°  132px
       *   M1 中上     -90.0°   80px
       *   M2 右上     -38.6°  145px
       *   M3 中左    -192.9°(≡167.1°)  91px
       *   M4 中右偏下  12.9°  132px
       *   M5 左下     115.7°  127px
       *   M6 右下偏下  64.3°  107px
       *
       * calcSize 自适应公式（与 CSS 一致）：min(Npx, N * 1.5 / 375 * 100vw)
       * dist 各自为 cr + r_i + gap，保证紧贴中心圆且不与之重叠。
       */
      var DEG_M = Math.PI / 180;
      var STEP_M = (360 / 7) * DEG_M; /* ≈ 51.43° in radians */

      /* calcSize for mobile: min(px, px * 1.5 / 375 * stageW)
       * 使用 var 函数表达式，避免在 if 块内使用 function 声明（部分浏览器严格模式会报错） */
      var mCalcSize = function (px) {
        return Math.min(px, ((px * 1.5) / 375) * stageW);
      };

      /* 7个槽：按正七边形顶点顺序（M3 排在最后使角度从 -90° 顺时针连续）
       * 视觉位置    角度                     目标直径
       */
      var mSlots = [
        { angle: -90 * DEG_M - STEP_M, diamPx: 132 }, // M0 左上
        { angle: -90 * DEG_M, diamPx: 80 }, // M1 中上
        { angle: -90 * DEG_M + STEP_M, diamPx: 145 }, // M2 右上
        { angle: -90 * DEG_M - 2 * STEP_M, diamPx: 91 }, // M3 中左
        { angle: -90 * DEG_M + 2 * STEP_M, diamPx: 132 }, // M4 中右偏下
        { angle: -90 * DEG_M - 3 * STEP_M, diamPx: 127 }, // M5 左下
        { angle: -90 * DEG_M + 3 * STEP_M, diamPx: 107 }, // M6 右下偏下
      ];

      var mPlaced = [];
      for (var k = 0; k < mSlots.length; k++) {
        var ms = mSlots[k];
        var mR = mCalcSize(ms.diamPx) / 2;
        var cosM = Math.cos(ms.angle);
        var sinM = Math.sin(ms.angle);
        var mDist = cr + mR + gap;
        mPlaced.push({ x: cx + cosM * mDist, y: cy + sinM * mDist, r: mR });
      }
      return mPlaced;
    }

    /* ── PC 确定性布局 ── */
    var DEG = Math.PI / 180;

    /* ── 辅助：给定圆心 (px, py)，计算在已放置障碍列表中允许的最大半径 ──
     * 返回值已 clamp 到 [minR, maxR]；若连 minR 都放不下返回 -1。
     */
    function maxFitR(px, py, obstacles) {
      var rMax = maxR;
      for (var oi = 0; oi < obstacles.length; oi++) {
        var o = obstacles[oi];
        var d = Math.sqrt((px - o.x) * (px - o.x) + (py - o.y) * (py - o.y));
        var avail = d - o.r - gap;
        if (avail < minR) return -1; // 放不下最小气泡
        if (avail < rMax) rMax = avail;
      }
      return rMax;
    }

    /* ── 障碍列表：从中心圆开始 ── */
    var obstacles = [{ x: cx, y: cy, r: cr }];

    /* ── 右侧5个槽定义（角度、inner 标志、目标半径）──
     * R0 右上内环  -45°  目标 246px（最大）
     * R1 正右内环    0°  目标 183px（第四大）
     * R2 右下内环  +45°  目标 215px（第三大，与R0同dist → 水平对齐）
     * R3 右外上   -20°  目标 223px（第二大，镜像后为左上）
     * R4 右外下   +20°  目标 120px（最小，镜像后为左下，与R3同dist → 水平对齐）
     *
     * targetR: 由 _initBubbles 用 calcSize 算好的自适应目标半径（上限值）。
     * 布局时以 targetR 为上限，通过 maxFitR 反推实际不重叠半径。
     */
    var slots0r =
      (pcSlotRadii && pcSlotRadii[0]) || minR + 0.85 * (maxR - minR);
    var slots1r =
      (pcSlotRadii && pcSlotRadii[1]) || minR + 0.55 * (maxR - minR);
    var slots2r =
      (pcSlotRadii && pcSlotRadii[2]) || minR + 0.75 * (maxR - minR);
    var slots3r = (pcSlotRadii && pcSlotRadii[3]) || minR + 0.8 * (maxR - minR);
    var slots4r = (pcSlotRadii && pcSlotRadii[4]) || minR;

    var rightSlots = [
      { angle: -45 * DEG, inner: true, targetR: slots0r }, // R0 右上  246px
      { angle: 0, inner: true, targetR: slots1r }, // R1 正右  183px
      { angle: 45 * DEG, inner: true, targetR: slots2r }, // R2 右下  215px（dist复用R0）
      { angle: -20 * DEG, inner: false, targetR: slots3r }, // R3 右外上 223px
      { angle: 20 * DEG, inner: false, targetR: slots4r }, // R4 右外下 120px（dist复用R3）
    ];

    var rightPlaced = [];
    /* R0/R2 共用 dist 和 r；R3/R4 共用 dist 和 r */
    var innerDist = -1; // R0 确定后供 R2 复用（水平对齐）
    var outerDist = -1; // R3 确定后供 R4 复用（水平对齐）
    /* R0 在障碍列表中的索引，供 R2 临时清零后恢复 */
    var r0ObstacleIdx = -1;

    for (var i = 0; i < rightSlots.length; i++) {
      var slot = rightSlots[i];
      var angle = slot.angle;
      var cosA = Math.cos(angle);
      var sinA = Math.sin(angle);

      var dist, r;

      if (i === 2) {
        /* R2：复用 R0 的 dist（保证水平对齐：x 相同，y 关于 cy 对称）。
         * R2 独立计算 r（targetR=215px），不回填 R0（R0 targetR=246px，各自独立）。 */
        dist = innerDist;
        var px2 = cx + cosA * dist;
        var py2 = cy + sinA * dist;

        /* 暂时清零 R0 障碍，避免 R0 与 R2 对称导致间距误判 */
        var r0Obstacle = obstacles[r0ObstacleIdx];
        var savedR0r = r0Obstacle.r;
        r0Obstacle.r = 0;

        var rFit2 = maxFitR(px2, py2, obstacles);

        /* 恢复 R0 障碍 */
        r0Obstacle.r = savedR0r;

        /* R2 独立 r，上限 targetR=215px */
        r = rFit2 < 0 ? minR : Math.min(rFit2, rightSlots[2].targetR);
        r = Math.max(r, minR);
      } else if (i === 4) {
        /* R4：复用 R3 的 dist（水平对齐），但 r 独立（targetR=120px）。
         * 暂时清零 R3 障碍，避免 R3 与 R4 位置对称导致间距误判。 */
        dist = outerDist;
        var px4 = cx + cosA * dist;
        var py4 = cy + sinA * dist;

        var r3ObstacleIdx = obstacles.length - 1; // R3 是最后加入的
        var r3Obstacle = obstacles[r3ObstacleIdx];
        var savedR3r = r3Obstacle.r;
        r3Obstacle.r = 0;

        var rFit4 = maxFitR(px4, py4, obstacles);

        /* 恢复 R3 障碍 */
        r3Obstacle.r = savedR3r;

        /* R4 独立 r，上限 targetR=120px */
        var rShared4 = Math.min(
          rFit4 < 0 ? minR : rFit4,
          rightSlots[4].targetR,
        );
        rShared4 = Math.max(rShared4, minR);
        r = rShared4;
      } else {
        /* ── 求 dist：以 targetR 为气泡半径，从 distMin 起步 ── */
        var targetR = slot.targetR;
        var distMin = slot.inner
          ? cr + targetR + gap
          : Math.max(cr * 2.2, cr + targetR + gap);

        /* distMax：不受 stage 边界硬限制（气泡允许被 section overflow:hidden 截断）。
         * 只设一个合理的最大展示范围：从中心到 stage 边缘的距离，给气泡留一点空间。
         * 这样在小屏 PC（769px~1200px）下 distMin > 边界约束时，气泡仍会按正确角度放置，
         * 而不是被 clamp 到错误位置。 */
        var distMax = Math.max(cx, stageH / 2) * 1.2;
        if (distMax < distMin) distMax = distMin;

        /* 取偏好位置（贴近 distMin） */
        var prefRatio = slot.inner ? 0.12 : 0.3;
        dist = distMin + prefRatio * (distMax - distMin);

        /* 由可用空间反推实际不重叠半径，上限用 targetR */
        var px0 = cx + cosA * dist;
        var py0 = cy + sinA * dist;
        var rFit = maxFitR(px0, py0, obstacles);
        r = rFit < 0 ? minR : Math.min(rFit, targetR);
        r = Math.max(r, minR);

        /* 若实际半径让 dist 不够，向外微调 dist */
        var minDistForR = cr + r + gap;
        if (dist < minDistForR) dist = minDistForR;

        if (i === 0) innerDist = dist; // 供 R2 复用（水平对齐）
        if (i === 3) outerDist = dist; // 供 R4 复用（水平对齐）
      }

      var x = cx + cosA * dist;
      var y = cy + sinA * dist;

      /* 不做 clamp：PC 端气泡允许超出 stage 边界，由 .lpl-section overflow:hidden 截断 */

      rightPlaced.push({ x: x, y: y, r: r });
      /* 将自身加入障碍，供后续气泡参考；记录 R0 的障碍索引供 R2 回填 */
      obstacles.push({ x: x, y: y, r: r });
      if (i === 0) r0ObstacleIdx = obstacles.length - 1;
    }

    /* 左侧：x 镜像，y / r 完全相同 */
    var leftPlaced = rightPlaced.map(function (p) {
      return { x: stageW - p.x, y: p.y, r: p.r };
    });

    /* 返回顺序：右侧5个 + 左侧5个 */
    return rightPlaced.concat(leftPlaced);
  }

  /* ── 复制到剪贴板 ────────────────────────────────────────────────────── */
  function _copyText(text) {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      return navigator.clipboard.writeText(text).catch(function () {
        _copyFallback(text);
      });
    }
    _copyFallback(text);
    return Promise.resolve();
  }

  function _copyFallback(text) {
    const el = document.createElement("textarea");
    el.value = text;
    el.style.cssText = "position:fixed;opacity:0;pointer-events:none;";
    document.body.appendChild(el);
    el.select();
    try {
      document.execCommand("copy");
    } catch (_) {}
    document.body.removeChild(el);
  }

  /* ── i18n 文案（从 #lottery-i18n 读取，与 26-pd 共用） ────────────── */
  function _i18n(ns) {
    if (!_i18n._cache) {
      const el = document.getElementById("lottery-i18n");
      try {
        _i18n._cache = JSON.parse((el && el.textContent) || "{}");
      } catch (_) {
        _i18n._cache = {};
      }
    }
    return (_i18n._cache && _i18n._cache[ns]) || {};
  }

  /* ── 校验图片 URL 是否合法（非空字符串且以 http/https 或 // 开头）── */
  function _isValidImgUrl(url) {
    if (!url || typeof url !== "string") return false;
    const s = url.trim();
    return (
      s.length > 0 &&
      (s.startsWith("http://") ||
        s.startsWith("https://") ||
        s.startsWith("//"))
    );
  }

  function _scrollToElement(el, alreadyLoaded) {
    const doScroll = () => {
      // 将 bfm pin timeline 推到末态，防止 scrub 拦截目标位置之前的滚动
      if (typeof ScrollTrigger !== "undefined") {
        const storySection = document.querySelector(".bfm-story-section");
        const pinST =
          storySection &&
          ScrollTrigger.getAll().find((st) => st.trigger === storySection);
        if (pinST && pinST.animation) {
          pinST.animation.progress(1, false);
          storySection.classList.add("story-board-animate-complete");
        }
      }

      const top = el.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({ top, behavior: "instant" });
    };

    // rAF + setTimeout(0)：跨 task 边界，确保 spacer layout 已提交再读位置
    const scheduleScroll = () =>
      requestAnimationFrame(() => setTimeout(doScroll, 0));

    if (typeof ScrollTrigger === "undefined" || alreadyLoaded) {
      scheduleScroll();
      return;
    }

    // 等 bfm autoRefreshEvents(load) 那次 refresh 完成后再滚
    let done = false;
    const onRefresh = () => {
      if (done) return;
      done = true;
      ScrollTrigger.removeEventListener("refresh", onRefresh);
      scheduleScroll();
    };
    ScrollTrigger.addEventListener("refresh", onRefresh);

    // 兜底：2s 内未收到 refresh，直接滚
    setTimeout(() => {
      if (done) return;
      done = true;
      ScrollTrigger.removeEventListener("refresh", onRefresh);
      scheduleScroll();
    }, 2000);
  }

  /* ── 构建集合商品 map（key: productId 字符串, value: product 对象）────
   *
   * 数据来源：Liquid 在 <script type="application/json" data-lpl-collection-products-json>
   * 中注入的 JSON。该 JSON 已在服务端完成集合遍历和商品去重（seen_ids 字符串去重）。
   *
   * @param {HTMLElement} rootEl      查找 data-lpl-collection-products-json 的根元素
   *                                  通常传 lpl-app 实例（this）
   * @param {string}      fallbackImg 当商品 featured_image 无效时使用的兜底图片 URL
   * @returns {Object}  { [productId: string]: productObject }
   *                    集合列表为空或未配置时返回空对象 {}
   * ─────────────────────────────────────────────────────────────────── */

  /* ══════════════════════════════════════════════════════════════════════
     Custom Element: lpl-app
     ══════════════════════════════════════════════════════════════════════ */
  if (!customElements.get("lpl-app")) {
    class LplApp extends HTMLElement {
      connectedCallback() {
        if (this._initialized) {
          /* 重新连接时（Shopify section re-render），重新触发气泡初始化 */
          requestAnimationFrame(() => {
            setTimeout(() => this._initBubbles(), 0);
          });
          return;
        }
        this._initialized = true;

        _ensureBase();

        this._sectionId = this.dataset.sectionId || "";
        this._activityType = this._resolveActivityType();
        this._stage = this.querySelector("[data-lpl-stage]");
        this._centerBubble = this.querySelector("[data-lpl-center-bubble]");
        this._bubblesCanvas = this.querySelector("[data-lpl-bubbles-canvas]");
        this._resultEl = this.querySelector("[data-lpl-result]");
        this._resultImg = this.querySelector("[data-lpl-result-img]");
        this._resultDiscountNum = this.querySelector("[data-lpl-discount-num]");
        this._resultDiscountUnit = this.querySelector(
          "[data-lpl-discount-unit]",
        );
        this._resultDesc = this.querySelector("[data-lpl-result-desc]");
        this._resultCodeText = this.querySelector("[data-lpl-code-text]");
        this._resultCopyBtn = this.querySelector("[data-lpl-copy-btn]");
        this._drawBtn = this.querySelector("[data-lpl-draw-btn]");
        this._resultProductWrap = this.querySelector(".lpl-result__product-wrap");

        /* 气泡数据（由 Liquid 注入到 data-bubbles JSON） */
        this._bubblesData = [];
        try {
          const raw = this.querySelector("[data-lpl-bubbles-json]");
          if (raw) this._bubblesData = JSON.parse(raw.textContent || "[]");
        } catch (_) {}

        /* 集合商品 map（由 Liquid 注入到 data-lpl-collection-products-json）
         * 结构：{ [productId: string]: productObject }
         * 构建时同步校验 featured_image，无效则以 recordPlaceholder 兜底 *

        this._isDrawing = false;
        this._currentCode = "";

        /* 绑定复制按钮 */
        if (this._resultCopyBtn) {
          this._resultCopyBtn.addEventListener("click", () =>
            this._handleCopy(),
          );
        }

        /* 绑定抽奖按钮 */
        if (this._drawBtn) {
          this._drawBtn.addEventListener("click", () => this._handleDraw());
        }

        /* 延迟初始化气泡（等 DOM 渲染完成，拿到真实宽高）
         * 用 rAF+setTimeout 组合代替 requestIdleCallback：
         * rAF 保证在下一帧 paint 之后执行，setTimeout 0 让浏览器先完成 layout */
        requestAnimationFrame(() => {
          setTimeout(() => this._initBubbles(), 0);
        });

        /* 登录成功后刷新状态 */
        _auth.onLogin(() => {
          /* nothing extra needed — next draw click will recheck */
        });

        /* 监听 auth 变化 */
        document.addEventListener("LUTE:AUTH:LOGIN", () => {
          _syncAuth();
        });

        /* ── 登录后自动抽奖 ──────────────────────────────────────────────
         * 场景：用户未登录点击抽奖 → 写入 sessionStorage 标记 → 唤起登录 →
         *       登录成功后页面刷新 → 此处检测到标记 → 滚动到组件 → 自动触发抽奖
         *
         * 匹配规则：标记值等于本实例的 sectionId，或为通用值 '1'
         *           （通用值兼容单组件页面，sectionId 值支持多实例区分）
         * ─────────────────────────────────────────────────────────────── */
        this._checkAutoDrawOnLoad();
      }

      disconnectedCallback() {
        /* cleanup */
      }

      /* ── 登录后自动操作（页面刷新后由 connectedCallback 调用）──────────
       * 场景A  未登录点击抽奖   → action:'draw'   → 刷新后自动抽奖
       * 场景B  未登录点击记录   → action:'record' → 刷新后自动打开中奖记录弹框
       * ─────────────────────────────────────────────────────────────────── */
      _checkAutoDrawOnLoad() {
        let payload;
        try {
          const raw = sessionStorage.getItem("lpl_auto_action");
          if (!raw) return;
          payload = JSON.parse(raw);
        } catch (_) {
          return;
        }

        if (!payload || !payload.action) return;

        const sid = payload.sectionId || "1";
        const isMatch = sid === "1" || sid === this._sectionId;
        if (!isMatch) return;

        try {
          sessionStorage.removeItem("lpl_auto_action");
        } catch (_) {}

        _syncAuth();
        if (!_auth || !_auth.user) return;

        const action = payload.action;

        const run = () => {
          setTimeout(() => {
            _scrollToElement(this, true);

            setTimeout(() => {
              if (action === "draw") {
                this._handleDraw();
              } else if (action === "record") {
                const recordPanel = this.querySelector("lpl-record-panel");
                if (recordPanel && typeof recordPanel._open === "function") {
                  recordPanel._open();
                }
              }
            }, 600);
          }, 400);
        };

        if (document.readyState === "complete") {
          run();
        } else {
          window.addEventListener(
            "load",
            () => {
              run();
            },
            { once: true },
          );
        }
      }

      /* ── 解析 activityType ──────────────────────────────────────────── */
      _resolveActivityType() {
        const norm = (this._sectionId || "").replace(/-/g, "_");
        return (
          window["__LPL_ACTIVITY_TYPE_" + norm + "__"] ||
          window["__LPL_ACTIVITY_TYPE_" + norm] ||
          window.__MC_ACTIVITY_TYPE__ ||
          ""
        );
      }

      /* ── 初始化产品气泡 ─────────────────────────────────────────────── */
      _initBubbles(attempt) {
        attempt = attempt || 0;

        /* DOM 节点可能在 connectedCallback 早期尚未就绪，重新查询 */
        if (!this._bubblesCanvas)
          this._bubblesCanvas = this.querySelector("[data-lpl-bubbles-canvas]");
        if (!this._stage) this._stage = this.querySelector("[data-lpl-stage]");

        if (!this._bubblesCanvas || !this._stage) {
          const delay = Math.min(100 * Math.pow(1.5, attempt), 1000);
          setTimeout(() => this._initBubbles(attempt + 1), delay);
          return;
        }

        const stageEl = this._stage;
        const stageW = stageEl.offsetWidth;
        const stageH = stageEl.offsetHeight;

        /* stage 尺寸未就绪：指数退避重试 */
        if (!stageW || !stageH) {
          const delay = Math.min(200 * Math.pow(1.5, attempt), 2000);
          setTimeout(() => this._initBubbles(attempt + 1), delay);
          return;
        }

        /* 气泡直径范围，与 CSS 保持一致的自适应公式
           PC:     min(Npx, N * 1.5 / 1920 * 100vw)
           Mobile: min(Npx, N * 2   / 375  * 100vw) */
        const vw = window.innerWidth;
        const isMobile = vw <= 768;

        function calcSize(px) {
          /* 其他尺寸公式：PC * 1.5 / 1920，Mobile * 1.5 / 375 */
          const ratio = 1.5;
          const base = isMobile ? 375 : 1920;
          return Math.min(px, (vw * px * ratio) / base);
        }

        /* 中心排斥半径 = 第5圈（最外层）：PC 435px，Mobile 275px */
        const centerTotalPx = isMobile ? 275 : 435;
        const totalBubbleSize = calcSize(centerTotalPx);
        const cr = totalBubbleSize / 2;
        const cx = stageW / 2;
        const cy = stageH / 2;

        /* 产品气泡直径范围：min=120px，max=246px（与需求规则一致） */
        const minR = calcSize(120) / 2;
        const maxR = calcSize(246) / 2;

        /* PC 端读取 footer 真实高度，作为底部安全边界（气泡不得进入 footer 区域）
           footer 是 position:absolute; bottom:0，所以其高度即为需要预留的空间 */
        let footerH = 0;
        if (!isMobile) {
          const footerEl = this.querySelector(".lpl-footer");
          if (footerEl) footerH = footerEl.offsetHeight;
        }

        /* PC 右侧5个槽的目标半径（直径/2），按槽顺序 R0~R4：
         *   R0 右上  246px（最大）
         *   R1 正右  183px（第四大）
         *   R2 右下  215px（第三大，镜像后为左下）
         *   R3 右外上 223px（第二大，镜像后为左上）
         *   R4 右外下 120px（最小）
         * 每个值用 calcSize 自适应换算为实际像素半径。
         */
        const pcSlotRadii = isMobile
          ? null
          : [
              calcSize(246) / 2, // R0 右上
              calcSize(183) / 2, // R1 正右
              calcSize(215) / 2, // R2 右下
              calcSize(223) / 2, // R3 右外上
              calcSize(120) / 2, // R4 右外下
            ];

        /* PC 10个（左5右5，X形），Mobile 7个（均匀环绕） */
        const n = isMobile ? 7 : 10;
        const positions = _layoutBubbles(
          stageW,
          stageH,
          cx,
          cy,
          cr,
          n,
          minR,
          maxR,
          isMobile,
          footerH,
          pcSlotRadii,
        );

        /* 布局结果为空（极端情况下 stage 尺寸异常）：稍后重试 */
        if (!positions || positions.length === 0) {
          const delay = Math.min(200 * Math.pow(1.5, attempt), 2000);
          setTimeout(() => this._initBubbles(attempt + 1), delay);
          return;
        }

        /* 清空旧气泡 */
        this._bubblesCanvas.innerHTML = "";

        positions.forEach((pos, idx) => {
          const bubbleData = this._bubblesData[idx] || {};
          const el = document.createElement("div");
          el.className = "lpl-product-bubble";
          el.setAttribute("aria-hidden", "true");

          /* 尺寸和位置（圆心转左上角） */
          const size = pos.r * 2;
          el.style.width = size + "px";
          el.style.height = size + "px";
          el.style.left = pos.x - pos.r + "px";
          el.style.top = pos.y - pos.r + "px";

          /* 随机动画参数（float 用） */
          el.style.setProperty(
            "--lpl-float-duration",
            (3 + Math.random() * 3).toFixed(2) + "s",
          );
          el.style.setProperty(
            "--lpl-float-delay",
            (-Math.random() * 3).toFixed(2) + "s",
          );

          /* 自定义气泡背景（配置了则用，否则用 CSS 默认渐变） */
          if (bubbleData.bg) {
            el.style.background =
              'url("' + bubbleData.bg + '") center / cover no-repeat';
            el.style.boxShadow = "none";
          }

          /* 产品图 */
          if (bubbleData.img) {
            const img = document.createElement("img");
            img.src = bubbleData.img;
            img.alt = bubbleData.alt || "";
            img.loading = "lazy";
            img.decoding = "async";
            el.appendChild(img);
          }

          this._bubblesCanvas.appendChild(el);
        });

        this._bubbleEls = Array.from(
          this._bubblesCanvas.querySelectorAll(".lpl-product-bubble"),
        );

        /* 窗口 resize 时重新布局，并同步更新 footer 高度变量 */
        if (!this._resizeHandler) {
          let resizeTimer;
          this._resizeHandler = () => {
            clearTimeout(resizeTimer);
            resizeTimer = setTimeout(() => {
              this._initBubbles();
              /* 若已展示结果，resize 后重新计算 footer 高度 */
              if (this._stage && this._stage.classList.contains("is-result")) {
                const footerEl = this.querySelector(".lpl-footer");
                if (footerEl && footerEl.offsetHeight > 0) {
                  this._stage.style.setProperty(
                    "--lpl-result-bottom",
                    footerEl.offsetHeight + "px",
                  );
                }
              }
            }, 300);
          };
          window.addEventListener("resize", this._resizeHandler, {
            passive: true,
          });
        }
      }

      /* ── 点击抽奖 ────────────────────────────────────────────────────── */
      async _handleDraw() {
        if (this._isDrawing) return;

        /* 1. 同步 auth */
        _syncAuth();

        /* 2. 未登录 → 登录入口已由 lpl-login-guard 拦截并写入 sessionStorage 标记，
         *    此处理论上不会执行，但保留作最后防线 */
        if (!_auth || !_auth.user) {
          _openLoginForLPL();
          return;
        }

        this._isDrawing = true;
        this._setDrawBtnDisabled(true);

        try {
          /* 3. 校验活动状态
           *    POST application/json，传 activityType
           *    返回结果 result === 0 → 校验通过；否则 → 提示并终止
           *    i18n key: bf25_blind_box.lactation_lottery.check_fail
           *              fallback: 'This activity is not available right now.'
           */
          const checkRes = await _fetch(
            "/api/station/activity/raffle/checkRandomDiscountStatus",
            {
              method: "POST",
              body: JSON.stringify({ activityType: this._activityType }),
            },
          );
          /* result 字段为 0 才视为通过，其余均视为失败 */
          const checkResult =
            checkRes && typeof checkRes === "object"
              ? checkRes.status
              : checkRes;
          if (Number(checkResult) !== 0) {
            this._isDrawing = false;
            this._setDrawBtnDisabled(false);
            const tips = _i18n("lactation_lottery");
            const msg =
              tips.check_fail_html ||
              "<strong>All today's discount slots have been claimed. Thank you for joining, and please come back tomorrow!</strong>";
            this._showTip(msg);
            return;
          }

          /* 4. 查询剩余抽奖次数
           *    GET application/x-www-form-urlencoded，传 activityType
           *    i18n key: bf25_blind_box.lactation_lottery.no_chances
           *              fallback: 'You have no chances left for today.'
           */
          const numRes = await _fetch(
            "/api/station/activity/raffle/queryRandomDiscountNumber?" +
              new URLSearchParams({
                activityType: this._activityType,
              }).toString(),
          );
          const chances =
            Number(
              numRes && typeof numRes === "object" ? numRes.result : numRes,
            ) || 0;

          if (chances < 1) {
            this._isDrawing = false;
            this._setDrawBtnDisabled(false);
            this._showNoChances(chances);
            return;
          }

          /* 5. 进入抽奖中动画状态 */
          this._enterDrawingState();

          /* 6. 获取购物车数据
           *    cartItems: [{ productId, variantId }, ...]
           */
          const cartItems = await _getCartItems();

          /* 构造 cartItems 数组，统一字段名为 variantId */
          const cartItemsPayload = cartItems.map(function (item) {
            return {
              productId: item.productId || "",
              variantId: item.varityId || item.variantId || "",
            };
          });

          /* 7. 正式抽奖
           *    POST application/json
           *    body: { activityType, cartItems: [{ productId, variantId }] }
           *    响应字段：result / prizeName / prizeUrl / prizeDesc / couponCode …
           */
          const prizeRes = await _fetch(
            "/api/station/activity/raffle/startRandomDiscount",
            {
              method: "POST",
              body: JSON.stringify({
                activityType: this._activityType,
                cartItems: cartItemsPayload,
              }),
            },
          );

          if (prizeRes.result <= -1) {
            this._isDrawing = false;
            this._setDrawBtnDisabled(false);
            this._showTodayNoChances(prizeRes.result);
            this._exitDrawingState();
            return;
          }

          /* 8. 展示结果 */
          this._showResult(prizeRes);
        } catch (err) {
          this._isDrawing = false;
          this._setDrawBtnDisabled(false);
          this._exitDrawingState();
          this._handleApiError(err);
        }
      }

      /* ── 进入抽奖中状态：启动气泡物理模拟 ─────────────────────────────── */
      _enterDrawingState() {
        if (!this._stage) return;
        this._stage.classList.add("is-drawing");
        this._startPhysics();
      }

      /* ── 退出抽奖中状态：停止物理模拟，恢复 CSS float 动画 ──────────── */
      _exitDrawingState() {
        if (!this._stage) return;
        this._stage.classList.remove("is-drawing");
        this._stopPhysics();
      }

      /* ── 物理模拟引擎 ────────────────────────────────────────────────────
       * 原理：
       *   每个气泡维护一个速度向量 (vx, vy)；每帧用 requestAnimationFrame 更新位置。
       *   碰撞检测：遍历所有气泡对，若圆心距 < r1+r2（相交），沿法线方向分离两圆，
       *   并将速度在法线方向的分量做弹性反射（切线速度保留，模拟"滑过"而非正碰弹开）。
       *   边界：气泡超出 stage 范围时速度反向。
       *   中心大气泡区域（cr = 第5圈半径）作为静态障碍，气泡不进入其中。
       * ─────────────────────────────────────────────────────────────────── */
      _startPhysics() {
        if (this._physicsRaf) return; /* 防止重复启动 */

        const els = this._bubbleEls;
        if (!els || els.length === 0) return;

        const stageEl = this._stage;
        const stageW = stageEl.offsetWidth;
        const stageH = stageEl.offsetHeight;
        const cx = stageW / 2;
        const cy = stageH / 2;

        /* 中心排斥半径（漂浮阶段用，防止气泡进入中心大圆） */
        const vw = window.innerWidth;
        const isMobile = vw <= 768;
        const centerPx = isMobile ? 275 : 435;
        const cr =
          Math.min(centerPx, (vw * centerPx * 1.5) / (isMobile ? 375 : 1920)) /
          2;

        /* 动画阶段时长
         *   float 阶段：约 1.2s 漂浮 + 轻碰撞
         *   transition 阶段：约 0.4s 加速切换到散开速度（平滑过渡）
         *   scatter 阶段：向外散开消失
         */
        const FLOAT_DURATION = 1200; /* ms */
        const TRANSITION_DURATION = 400; /* ms */
        let startTs = null; /* 第一帧时间戳，用于计算阶段进度 */

        /* 为每个气泡建立物理状态 */
        const bubbles = els.map((el) => {
          const r = parseFloat(el.style.width) / 2;
          const originLeft = el.style.left;
          const originTop = el.style.top;
          const x = parseFloat(originLeft) + r;
          const y = parseFloat(originTop) + r;
          el.style.transform = "none";

          /* 漂浮阶段初速度：随机方向，中等速度（1.2~2.2 px/帧），确保有足够动能发生明显碰撞 */
          const floatAngle = Math.random() * Math.PI * 2;
          const floatSpeed = 1.2 + Math.random() * 1.0;

          /* 散开阶段目标速度：径向向外 + ±35° 随机偏角，快速（3~5 px/帧） */
          const radialAngle = Math.atan2(y - cy, x - cx);
          const scatterSpread = (Math.random() - 0.5) * ((Math.PI * 35) / 180);
          const scatterAngle = radialAngle + scatterSpread;
          const scatterSpeed = 3.0 + Math.random() * 2.0;

          return {
            el,
            r,
            x,
            y,
            originLeft,
            originTop,
            vx: Math.cos(floatAngle) * floatSpeed,
            vy: Math.sin(floatAngle) * floatSpeed,
            /* 散开目标速度，transition 阶段线性插值到此值 */
            svx: Math.cos(scatterAngle) * scatterSpeed,
            svy: Math.sin(scatterAngle) * scatterSpeed,
          };
        });

        /* 淡出范围：距 stage 边缘 fadeZone px 内开始渐隐（仅散开阶段） */
        const fadeZone = Math.min(stageW, stageH) * 0.28;

        let lastTime = null;

        /* ── 碰撞处理（两阶段共用）── */
        const resolveCollisions = (restitution) => {
          for (let i = 0; i < bubbles.length; i++) {
            for (let j = i + 1; j < bubbles.length; j++) {
              const a = bubbles[i];
              const b = bubbles[j];
              const dx = b.x - a.x;
              const dy = b.y - a.y;
              const dist = Math.sqrt(dx * dx + dy * dy) || 0.001;
              const minDist = a.r + b.r;
              if (dist < minDist) {
                const nx = dx / dist;
                const ny = dy / dist;
                const overlap = (minDist - dist) / 2;
                a.x -= nx * overlap;
                a.y -= ny * overlap;
                b.x += nx * overlap;
                b.y += ny * overlap;
                const dvx = b.vx - a.vx;
                const dvy = b.vy - a.vy;
                const dot = dvx * nx + dvy * ny;
                if (dot < 0) {
                  const impulse = ((1 + restitution) * dot) / 2;
                  a.vx += impulse * nx;
                  a.vy += impulse * ny;
                  b.vx -= impulse * nx;
                  b.vy -= impulse * ny;
                }
              }
            }
          }
        };

        /* ── 中心圆排斥（漂浮阶段）── */
        const repelCenter = () => {
          for (const b of bubbles) {
            const dx = b.x - cx;
            const dy = b.y - cy;
            const dist = Math.sqrt(dx * dx + dy * dy) || 0.001;
            const minD = cr + b.r + 6;
            if (dist < minD) {
              const nx = dx / dist;
              const ny = dy / dist;
              b.x = cx + nx * minD;
              b.y = cy + ny * minD;
              const dot = b.vx * nx + b.vy * ny;
              if (dot < 0) {
                b.vx -= 1.2 * dot * nx;
                b.vy -= 1.2 * dot * ny;
              }
            }
          }
        };

        /* ── 舞台软边界反弹（漂浮阶段，让气泡在 stage 内来回） ── */
        const bounceBounds = () => {
          for (const b of bubbles) {
            if (b.x - b.r < 0) {
              b.x = b.r;
              b.vx = Math.abs(b.vx);
            } else if (b.x + b.r > stageW) {
              b.x = stageW - b.r;
              b.vx = -Math.abs(b.vx);
            }
            if (b.y - b.r < 0) {
              b.y = b.r;
              b.vy = Math.abs(b.vy);
            } else if (b.y + b.r > stageH) {
              b.y = stageH - b.r;
              b.vy = -Math.abs(b.vy);
            }
          }
        };

        const tick = (ts) => {
          if (!this._physicsRaf) return;
          if (!startTs) startTs = ts;

          const elapsed = ts - startTs;
          const dt = lastTime ? Math.min((ts - lastTime) / 16.67, 3) : 1;
          lastTime = ts;

          if (elapsed < FLOAT_DURATION) {
            /* ── 阶段一：漂浮 + 轻碰撞 ── */
            for (const b of bubbles) {
              b.x += b.vx * dt;
              b.y += b.vy * dt;
              /* 随机扰动：增大幅度让运动更不规律，碰撞更难预测 */
              if (Math.random() < 0.04) {
                b.vx += (Math.random() - 0.5) * 0.6;
                b.vy += (Math.random() - 0.5) * 0.6;
              }
              /* 速度限幅：漂浮阶段不超过 3.0 px/帧 */
              const spd = Math.sqrt(b.vx * b.vx + b.vy * b.vy);
              if (spd > 3.0) {
                b.vx = (b.vx / spd) * 3.0;
                b.vy = (b.vy / spd) * 3.0;
              }
            }
            resolveCollisions(0.75); /* 高弹性，碰撞后明显弹开 */
            repelCenter();
            bounceBounds();

            /* 全程保持基础 opacity */
            for (const b of bubbles) {
              b.el.style.opacity = "0.55";
              b.el.style.left = (b.x - b.r).toFixed(2) + "px";
              b.el.style.top = (b.y - b.r).toFixed(2) + "px";
            }
          } else if (elapsed < FLOAT_DURATION + TRANSITION_DURATION) {
            /* ── 阶段二：速度平滑过渡到散开速度（easeIn）── */
            const t = (elapsed - FLOAT_DURATION) / TRANSITION_DURATION;
            const ease = t * t; /* easeIn：开始慢，越来越快 */

            for (const b of bubbles) {
              b.vx = b.vx + (b.svx - b.vx) * ease * 0.25 * dt;
              b.vy = b.vy + (b.svy - b.vy) * ease * 0.25 * dt;
              b.x += b.vx * dt;
              b.y += b.vy * dt;
              b.el.style.opacity = "0.55";
              b.el.style.left = (b.x - b.r).toFixed(2) + "px";
              b.el.style.top = (b.y - b.r).toFixed(2) + "px";
            }
            resolveCollisions(0.4);
          } else {
            /* ── 阶段三：全速散开 + 边缘渐隐 ── */
            for (const b of bubbles) {
              b.x += b.vx * dt;
              b.y += b.vy * dt;
              b.vx *= 0.991; /* 轻微减速，飞出边缘前不会骤停 */
              b.vy *= 0.991;
            }
            resolveCollisions(0.5);

            for (const b of bubbles) {
              b.el.style.left = (b.x - b.r).toFixed(2) + "px";
              b.el.style.top = (b.y - b.r).toFixed(2) + "px";
              const distToEdge = Math.min(b.x, stageW - b.x, b.y, stageH - b.y);
              if (distToEdge < fadeZone) {
                b.el.style.opacity = (
                  Math.max(0, distToEdge / fadeZone) * 0.55
                ).toFixed(3);
              } else {
                b.el.style.opacity = "0.55";
              }
            }
          }

          this._physicsRaf = requestAnimationFrame(tick);
        };

        this._physicsRaf = requestAnimationFrame(tick);
        this._physicsBubbles = bubbles;
      }

      _stopPhysics() {
        if (this._physicsRaf) {
          cancelAnimationFrame(this._physicsRaf);
          this._physicsRaf = null;
        }
        /* 清除物理期间的 transform:none，让 CSS float 动画重新接管
         * left/top 也恢复到初始布局值（存在 _physicsOrigin 里） */
        if (this._physicsBubbles) {
          for (const b of this._physicsBubbles) {
            b.el.style.transform = "";
            b.el.style.left = b.originLeft;
            b.el.style.top = b.originTop;
            b.el.style.opacity = "";
          }
          this._physicsBubbles = null;
        }
      }

      /* ── 展示中奖结果 ────────────────────────────────────────────────── */
      _showResult(prizeRes) {
        /* 解析 extraInfo JSON 字符串
         * 字段：discountValue / discountDesc / remainHours / expireTimeStore /
         *       expireTimestamp / validStatus / valueType / priceRuleType / storeTimezone
         */
        let extraInfo = {};
        try {
          if (prizeRes && prizeRes.extraInfo) {
            extraInfo = JSON.parse(prizeRes.extraInfo);
          }
        } catch (_) {}

        const discountText = (prizeRes && prizeRes.prizeName) || "";
        const couponCode =
          (prizeRes && (prizeRes.couponCode || prizeRes.prizeCode)) || "";

        /* 产品图：用 productId 从集合商品 map 匹配 featured_image，
         * 其次接口 prizeUrl 字段，最后兜底占位图（与中奖记录复用同一配置）*/
        const productId =
          prizeRes && prizeRes.productId ? String(prizeRes.productId) : "";
        const mapProduct =
          productId && _collectionProductMap
            ? _collectionProductMap[productId]
            : null;
        const placeholderImg = this.dataset.recordPlaceholder || "";
        const productImg =
          (mapProduct && mapProduct.featured_image) ||
          (prizeRes && prizeRes.prizeUrl) ||
          placeholderImg;

        /* 折扣数字：优先 extraInfo.discountValue，兜底从 prizeName 解析 */
        const discountNum =
          extraInfo.discountValue !== undefined &&
          extraInfo.discountValue !== null
            ? String(extraInfo.discountValue)
            : (() => {
                const m = discountText.match(/(\d+)/);
                return m ? m[1] : "";
              })();

        /* 折扣单位：固定 OFF（% 符号已在 HTML 里写死） */
        const discountUnit = "OFF";

        /* desc：优先 extraInfo.remainHours 拼装"Limited-time offer: N hours"，
         * 兜底 prizeDesc 或 prizeName */
        const desc =
          extraInfo.remainHours !== undefined && extraInfo.remainHours !== null
            ? "Limited\u2011time offer: " + extraInfo.remainHours + " hours"
            : (prizeRes && prizeRes.prizeDesc) || discountText || "";

        this._currentCode = couponCode;

        /* 中奖产品 URL（用于点击跳转）：从集合商品 map 中取 */
        const productUrl = (mapProduct && mapProduct.url) || "";

        /* 更新结果 UI */
        if (this._resultImg && productImg) {
          this._resultImg.src = productImg;
          this._resultImg.alt = discountText || "Prize product";
        }

        /* 产品图区域点击跳转：有 URL 则添加跳转，否则清除 */
        if (this._resultProductWrap) {
          /* 移除旧的点击事件（避免重复绑定） */
          if (this._resultProductWrapClickHandler) {
            this._resultProductWrap.removeEventListener(
              "click",
              this._resultProductWrapClickHandler,
            );
            this._resultProductWrapClickHandler = null;
          }
          if (productUrl) {
            this._resultProductWrap.style.cursor = "pointer";
            this._resultProductWrapClickHandler = function () {
              window.open(productUrl, "_blank", "noopener,noreferrer");
            };
            this._resultProductWrap.addEventListener(
              "click",
              this._resultProductWrapClickHandler,
            );
          } else {
            this._resultProductWrap.style.cursor = "";
          }
        }
        if (this._resultDiscountNum)
          this._resultDiscountNum.textContent = discountNum;
        if (this._resultDiscountUnit) {
          /* % 固定在 HTML .lpl-result__discount-pct 里，只更新 off 文字 */
          const offEl = this._resultDiscountUnit.querySelector(
            ".lpl-result__discount-off",
          );
          if (offEl) offEl.textContent = discountUnit;
        }
        if (this._resultDesc) this._resultDesc.textContent = desc;
        if (this._resultCodeText) this._resultCodeText.textContent = couponCode;
        if (this._resultCopyBtn) this._resultCopyBtn.textContent = "Copy";

        /* 切换到结果状态（CSS 控制过渡） */
        if (this._stage) {
          /* 动态读取 footer 高度，注入 CSS 变量，确保 lpl-result 底部不与 footer 重叠 */
          const footerEl = this.querySelector(".lpl-footer");
          if (footerEl) {
            const fh = footerEl.offsetHeight;
            if (fh > 0) {
              this._stage.style.setProperty("--lpl-result-bottom", fh + "px");
            }
          }
          this._stage.classList.remove("is-drawing");
          this._stage.classList.add("is-result");
        }

        this._isDrawing = false;
        /* 结果展示时不恢复按钮，用户已无法再次抽奖 */
      }

      /* ── 处理 Copy / Copied ──────────────────────────────────────────── */
      _handleCopy() {
        if (!this._currentCode) return;
        _copyText(this._currentCode);
        if (this._resultCopyBtn) {
          this._resultCopyBtn.textContent = "Copied";
          /* 不永久变为 Copied，后续仍可继续复制（但 Copied 提示持续） */
          /* 根据需求：点击后变 Copied，但后面继续点照样可以复制（Copied 状态保持） */
        }
      }

      /* ── 无抽奖次数提示 ──────────────────────────────────────────────── */
      _showNoChances(num) {
        /* i18n key: bf25_blind_box.lactation_lottery.tips.no_chances_html
         * fallback: 'You have no chances left for today.' */
        const tips = _i18n("lactation_lottery");
        const msg =
          num === -2
            ? tips.no_chances_html_two ||
              "<strong>Done for today. See you tomorrow!</strong>"
            : tips.no_chances_html ||
              "<strong>You've used up all your chances for now!</strong>";
        this._showTip(msg);
      }

      _showTodayNoChances(num) {
        /* i18n key: bf25_blind_box.lactation_lottery.tips.no_chances_html
         * fallback: 'You have no chances left for today.' */
        const tips = _i18n("lactation_lottery");
        const msg =
          num == -2
            ? tips.no_chances_html_two ||
              "<strong>Done for today. See you tomorrow!</strong>"
            : tips.today_no_chances ||
              "<strong>All today's discount slots have been claimed. Thank you for joining, and please come back tomorrow!</strong>";
        this._showTip(msg);
      }

      /* ── API 异常处理 ────────────────────────────────────────────────── */
      _handleApiError(err) {
        /* i18n key 对应关系（均在 bf25_blind_box.lactation_lottery.tips 命名空间下）：
         *   code 10004270 → tips.ended_html          活动已结束
         *   code 10004269 → tips.not_started_html    活动未开始
         *   code 10004271 → tips.no_today_html       需选择今天日期
         *   code 10001003 → 登录失效，重新唤起登录
         *   check_fail    → tips.check_fail_html     校验失败（_handleDraw 内直接调用）
         *   其余           → tips.draw_error_html     通用抽奖错误
         */
        const tips = _i18n("lactation_lottery");
        const code = err && err.code;
        let msg = "";
        if (code === "10004270")
          msg = tips.ended_html || "<strong>The event has ended!</strong>";
        else if (code === "10004269")
          msg =
            tips.not_started_html ||
            "<strong>This event is coming soon!</strong>";
        else if (code === "10004271")
          msg =
            tips.no_today_html ||
            "<strong>Select today's date to unlock your draw!</strong>";
        else if (code === "10001003") {
          if (_auth) _auth.user = null;
          document.dispatchEvent(new CustomEvent("lute:account:logout"));
          if (_login) _login();
          return;
        } else {
          msg =
            tips.draw_error_html ||
            tips.next_time_error_html ||
            "Sorry, something went wrong. Please try again.";
        }
        this._showTip(msg);
      }

      /* ── 简单 tip 提示（toast 样式） ────────────────────────────────── */
      _showTip(html) {
        const el = document.createElement("div");
        el.className = "m26-toast";
        /* m26-mystery-box.css 中 .m26-toast 是 position:absolute，
         * append 到 body 后 top/left 50% 是相对于整个文档高度，
         * 当页面很长时 toast 会出现在视口之外，用 fixed 覆盖确保始终在视口内 */
        el.style.cssText =
          "position:fixed;max-width:300px;text-align:center;white-space:normal;";
        el.innerHTML = '<span class="m26-toast__text">' + html + "</span>";
        document.body.appendChild(el);
        requestAnimationFrame(() => el.classList.add("is-visible"));
        setTimeout(() => {
          el.classList.remove("is-visible");
          el.addEventListener("transitionend", () => el.remove(), {
            once: true,
          });
          setTimeout(() => el.remove(), 400);
        }, 4000);
      }

      /* ── 禁用/启用抽奖按钮 ──────────────────────────────────────────── */
      _setDrawBtnDisabled(disabled) {
        if (this._drawBtn) this._drawBtn.disabled = disabled;
      }
    }

    customElements.define("lpl-app", LplApp);
  }

  /* ══════════════════════════════════════════════════════════════════════
     Custom Element: lpl-record-panel（Prize Records 弹框）
     复用与 26-pd-promotion-lottery.js 中 m26-record-panel 相同的逻辑
     ══════════════════════════════════════════════════════════════════════ */
  if (!customElements.get("lpl-record-panel")) {
    class LplRecordPanel extends HTMLElement {
      connectedCallback() {
        if (this._init) return;
        this._init = true;
        _ensureBase();
        this._modal = null;
        this.addEventListener("click", this);
      }
      disconnectedCallback() {
        this.removeEventListener("click", this);
        if (this._modal) {
          this._modal.destroy();
          this._modal = null;
        }
      }
      handleEvent(e) {
        if (e.type !== "click") return;
        if (e.target.closest(".m26-modal-overlay")) return;
        this._open();
      }
      _open() {
        _syncAuth();
        if (!_auth || !_auth.user) {
          _openLoginForLPL();
          return;
        }
        const tpl = this.querySelector("template");
        if (!tpl) return;
        if (!this._modal) this._modal = new _Modal("lpl-modal-box lpl-modal");
        const content = tpl.content.cloneNode(true);
        const wrapper = document.createElement("div");
        wrapper.appendChild(content);
        this._modal.setContent(wrapper.innerHTML);
        this._modal.show();
        /* M26Modal.show() 同步写入 _bodyEl.innerHTML，双 rAF 确保 DOM 完全稳定后再查询 */
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            const bodyEl = this._modal && this._modal._bodyEl;
            if (bodyEl) this._loadRecords(bodyEl);
          });
        });
      }
      async _loadRecords(body) {
        const app = this.closest("lpl-app");
        const activityType =
          (app && app._activityType) || window.__MC_ACTIVITY_TYPE__ || "";
        const listEl = body.querySelector("[data-lpl-records-list]");
        const emptyEl = body.querySelector("[data-lpl-records-empty]");
        if (!listEl || !emptyEl) return;

        /* 注入骨架屏：插入到 listEl 的父节点（lpl-modal__body）内、listEl 之前
         * listEl 是 body(_bodyEl) 的孙子元素，不能直接 body.insertBefore */
        const skeletonHtml =
          '<div class="lpl-skeleton" aria-hidden="true">' +
          '<div class="lpl-skeleton__card"></div>' +
          '<div class="lpl-skeleton__card"></div>' +
          '<div class="lpl-skeleton__card"></div>' +
          "</div>";
        const skeletonEl = document.createElement("div");
        skeletonEl.innerHTML = skeletonHtml;
        const skeleton = skeletonEl.firstChild;
        listEl.parentNode.insertBefore(skeleton, listEl);

        /* 读取占位图 URL（从 lpl-app 的 data-record-placeholder 注入） */
        const placeholderImg =
          (app && app.dataset && app.dataset.recordPlaceholder) || "";

        /* 读取卡片背景图 URL（从 lpl-record-panel 的 data 属性注入） */
        const isMobile = window.innerWidth <= 768;
        const bgActiveUrl = isMobile
          ? this.dataset.bgActiveMobile || this.dataset.bgActivePc || ""
          : this.dataset.bgActivePc || "";
        const bgInactiveUrl = isMobile
          ? this.dataset.bgInactiveMobile || this.dataset.bgInactivePc || ""
          : this.dataset.bgInactivePc || "";

        try {
          /* GET application/x-www-form-urlencoded
           * 返回结构：{ total, pages, code, message, recordBeans: [ { prizeName, prizeImag,
           *   prizeDesc, prizeCode, couponCode, sendPrizeStatus, prizeTime, prizeTimeDay,
           *   activityUserPrizeCode, prizeType, extraInfo, ... } ] }
           */
          const res = await _fetch(
            "/api/station/activity/raffle/queryRandomDiscountWinningRecord?" +
              new URLSearchParams({ activityType: activityType }).toString(),
          );

          /* recordBeans 是标准列表字段，兜底兼容旧字段 */
          const records =
            (res && (res.recordBeans || res.records || res.list)) || [];
          /* 移除骨架屏 */
          if (skeleton && skeleton.parentNode) skeleton.remove();
          if (!Array.isArray(records) || !records.length) {
            listEl.hidden = true;
            emptyEl.hidden = false;
            return;
          }
          emptyEl.hidden = true;
          listEl.hidden = false;
          listEl.innerHTML = "";
          const i18n = _i18n("lactation_lottery");
          records.forEach((item) => {
            const li = document.createElement("li");

            /* couponCode 优先，prizeCode 兜底 */
            const code = item.couponCode || item.prizeCode || "";

            /* 解析 extraInfo JSON 字符串
             * 字段：discountDesc / discountValue / expireTimeStore / expireTimestamp /
             *       remainHours / validStatus / valueType / priceRuleType / storeTimezone
             * validStatus: 1=有效，0=已过期
             */
            let ei = {};
            try {
              if (item.extraInfo) ei = JSON.parse(item.extraInfo);
            } catch (_) {}

            /* 过期判断：优先 extraInfo.validStatus（0=过期），兜底 sendPrizeStatus */
            const expired =
              ei.validStatus !== undefined
                ? ei.validStatus === 0
                : item.sendPrizeStatus !== undefined
                  ? item.sendPrizeStatus !== 0
                  : false;

            /* ── 用 productId 从集合商品 map 中查找对应产品信息 ──────────────
             * 优先级：collectionProductMap[productId] > 接口返回字段
             * productId 可能是数字或字符串，统一转为字符串后匹配
             */
            const productId = item.productId ? String(item.productId) : "";
            const mapProduct =
              productId && _collectionProductMap
                ? _collectionProductMap[productId]
                : null;
            /* 产品名称：优先 map 中的 title，其次接口返回的 prizeName */
            const name =
              (mapProduct && mapProduct.title) || item.prizeName || "";

            /* 产品图：优先 map 中的 featured_image，其次接口返回字段，无则使用占位图 */
            const prizeImg =
              (mapProduct && mapProduct.featured_image) ||
              item.prizeImag ||
              item.prizeImg ||
              item.prizeUrl ||
              placeholderImg;

            li.className =
              "lpl-record__card" +
              (expired ? " lpl-record__card--expired" : "");

            /* 背景图样式 */
            const bgUrl = expired ? bgInactiveUrl : bgActiveUrl;
            const bgStyle = bgUrl
              ? " style=\"background-image:url('" +
                bgUrl.replace(/'/g, "%27") +
                "')\""
              : "";

            /* 折扣展示：优先 extraInfo.discountDesc，兜底 prizeName */
            const discountDisplay = ei.discountValue || item.prizeName || "";

            /* 有效期：优先 extraInfo.expireTimeStore（直接展示，无需 Date 解析）*/
            const validText = ei.expireTimeStore || "";

            /* 折扣拆分：将 discountDisplay（如 "63% off"）拆为数值部分和 OFF 部分
             * 数值部分放入 .lpl-record__discount-num，OFF 单独放入 .lpl-record__discount-off
             * 匹配：数字+可选% 为主体，后跟可选空格+off/OFF */
            let discountMain = discountDisplay + "%";

            /* 底部操作区：可用 → 折扣码+Copy；已使用/过期 → Expired 按钮 */
            let actionHtml;
            if (expired) {
              actionHtml =
                '<button type="button" class="lpl-record__expired-btn" disabled>' +
                (i18n.expired || "Expired") +
                "</button>";
            } else if (code) {
              actionHtml =
                '<button type="button" class="lpl-record__copy-btn" data-lpl-copy="' +
                _esc(code) +
                '">' +
                '<span class="lpl-record__code-text">' +
                _esc(code) +
                "</span>" +
                '<span class="lpl-record__code-divider" aria-hidden="true"></span>' +
                '<span class="lpl-record__copy-label">' +
                (i18n.copy || "Copy") +
                "</span>" +
                "</button>";
            } else {
              actionHtml = "";
            }

            li.innerHTML =
              '<div class="lpl-record__coupon"' +
              bgStyle +
              ">" +
              '<div class="lpl-record__card-top">' +
              '<span class="lpl-record__brand">' +
              _BRAND_SVG +
              "</span>" +
              (prizeImg
                ? (mapProduct && mapProduct.url
                    ? '<a class="lpl-record__prize-link" href="' + _esc(mapProduct.url) + '" target="_blank" rel="noopener">' +
                      '<img class="lpl-record__prize-img" src="' + _esc(prizeImg) + '" alt="' + _esc(name) + '" loading="lazy">' +
                      '</a>'
                    : '<img class="lpl-record__prize-img" src="' + _esc(prizeImg) + '" alt="' + _esc(name) + '" loading="lazy">')
                : "") +
              '<p class="lpl-record__prize-name">' +
              _esc(name) +
              "</p>" +
              "</div>" +
              '<div class="lpl-record__card-bottom">' +
              (discountMain
                ? '<p class="lpl-record__discount">' +
                  '<span class="lpl-record__discount-num">' +
                  _esc(discountMain) +
                  "</span>" +
                  '<span class="lpl-record__discount-off">OFF</span>' +
                  "</p>"
                : "") +
              "</div>" +
              "</div>" +
              '<div class="lpl-record__meta">' +
              (validText
                ? '<p class="lpl-record__valid">Valid until: ' +
                  _esc(validText) +
                  "</p>"
                : "") +
              '<div class="lpl-record__action">' +
              actionHtml +
              "</div>" +
              "</div>";

            listEl.appendChild(li);
          });

          /* 复制按钮事件 */
          listEl.querySelectorAll("[data-lpl-copy]").forEach((btn) => {
            btn.addEventListener("click", () => {
              const code = btn.dataset.lplCopy;
              _copyText(code);
              const label = btn.querySelector(".lpl-record__copy-label");
              if (label) label.textContent = "Copied";
            });
          });
        } catch (_err) {
          if (skeleton && skeleton.parentNode) skeleton.remove();
          listEl.hidden = true;
          emptyEl.hidden = false;
        }
      }
    }

    /* momcozy brand SVG logo（用于中奖记录卡片 lpl-record__brand）*/
    var _BRAND_SVG =
      '<svg xmlns="http://www.w3.org/2000/svg" width="61" height="10" viewBox="0 0 61 10" fill="none" aria-hidden="true" focusable="false">' +
      '<path d="M7.39006 1.53371C8.81371 1.53381 9.96802 2.66903 9.96802 4.06985V8.79325C9.96796 8.89504 9.88395 8.97762 9.78047 8.97772H8.77883C8.67529 8.97768 8.59088 8.89508 8.59082 8.79325V4.06985C8.59082 3.4175 8.05326 2.88843 7.39006 2.88832C6.72677 2.88832 6.18884 3.41744 6.18884 4.06985V7.42497C6.18881 7.52685 6.10442 7.60989 6.00083 7.60989H4.99965C4.89606 7.60989 4.81167 7.52685 4.81165 7.42497V4.07714C4.81165 3.42084 4.27025 2.88834 3.60301 2.88832C2.93576 2.88832 2.39438 3.42034 2.39438 4.07714L2.39392 8.79325C2.39386 8.89511 2.30948 8.97772 2.20591 8.97772H1.20473C1.10116 8.97772 1.01678 8.89511 1.01672 8.79325V4.07714C1.01672 2.67235 2.17435 1.53371 3.60255 1.53371C4.30299 1.53373 4.93798 1.80733 5.40346 2.25201C5.456 2.30219 5.54035 2.30172 5.59239 2.25201C6.05689 1.80731 6.69108 1.53371 7.39006 1.53371Z" fill="#6A2B3A"/>' +
      '<path d="M26.0062 1.53371C27.43 1.53371 28.5847 2.66897 28.5847 4.06985V8.79325C28.5846 8.89511 28.5002 8.97772 28.3967 8.97772H27.3955C27.2919 8.97771 27.2075 8.8951 27.2075 8.79325V4.06985C27.2075 3.41744 26.6695 2.88832 26.0062 2.88832C25.3431 2.88844 24.8055 3.41751 24.8055 4.06985V7.42497C24.8055 7.52684 24.721 7.60986 24.6175 7.60989H23.6158C23.5123 7.60978 23.4283 7.52679 23.4283 7.42497V4.07714C23.4283 3.42085 22.8869 2.88837 22.2197 2.88832C21.5524 2.88832 21.011 3.42034 21.011 4.07714L21.0106 8.79325C21.0105 8.89511 20.9261 8.97772 20.8226 8.97772H19.8214C19.7178 8.97772 19.6334 8.89511 19.6334 8.79325V4.07714C19.6334 2.67235 20.791 1.53371 22.2192 1.53371C22.9196 1.53374 23.5546 1.80734 24.0201 2.25201C24.0727 2.30217 24.157 2.30172 24.209 2.25201C24.6735 1.80736 25.3074 1.53376 26.0062 1.53371Z" fill="#6A2B3A"/>' +
      '<path d="M45.7287 1.53371H51.1338C51.2329 1.53371 51.3252 1.58386 51.3783 1.66625L51.9905 2.62095C52.0212 2.68775 52.0088 2.7592 51.9692 2.81089C51.9617 2.824 51.9523 2.83661 51.9409 2.84778L47.3949 7.47826C47.3401 7.53435 47.3805 7.62766 47.4597 7.62766H51.8363C51.9404 7.62766 52.0247 7.71064 52.0247 7.81304V8.79279C52.0246 8.89512 51.9403 8.97772 51.8363 8.97772H45.4009C45.2968 8.97772 45.2125 8.89512 45.2124 8.79279V7.79027C45.2099 7.74107 45.2276 7.69215 45.2647 7.65316L49.801 3.03317C49.856 2.97709 49.8155 2.88331 49.7362 2.88331H45.7283C45.6242 2.88323 45.5403 2.80027 45.5403 2.69793V1.71863C45.5403 1.61629 45.6242 1.53333 45.7283 1.53325L45.7287 1.53371Z" fill="#6A2B3A"/>' +
      '<path fill-rule="evenodd" clip-rule="evenodd" d="M14.8262 1.54054C16.9141 1.54062 18.6063 3.20539 18.6063 5.25913C18.6062 7.31281 16.9141 8.97718 14.8262 8.97726C12.7382 8.97726 11.0457 7.31285 11.0456 5.25913C11.0456 3.20534 12.7381 1.54054 14.8262 1.54054ZM14.8262 2.89515C13.4991 2.89515 12.4228 3.95381 12.4228 5.25913C12.4229 6.56438 13.4991 7.6231 14.8262 7.6231C16.1536 7.62302 17.2295 6.56433 17.2295 5.25913C17.2295 3.95386 16.1532 2.89523 14.8262 2.89515Z" fill="#6A2B3A"/>' +
      '<path d="M33.4229 1.54054C34.4679 1.54054 35.414 1.95756 36.0981 2.63143C36.1534 2.68647 36.1613 2.7716 36.1194 2.83685L35.5563 3.71457C35.4894 3.81892 35.3352 3.8214 35.2544 3.72778C34.8137 3.21824 34.1566 2.89515 33.4229 2.89515C32.0959 2.8952 31.0195 3.95384 31.0195 5.25913C31.0196 6.56435 32.0954 7.62306 33.4229 7.6231C34.155 7.6231 34.8109 7.30069 35.2516 6.79321C35.3329 6.69959 35.4871 6.70252 35.5535 6.80687L36.1143 7.68687C36.1559 7.75215 36.1479 7.83724 36.0925 7.89184C35.4089 8.5623 34.4649 8.97726 33.4229 8.97726C31.335 8.97722 29.6424 7.31283 29.6423 5.25913C29.6423 3.20537 31.3349 1.54058 33.4229 1.54054Z" fill="#6A2B3A"/>' +
      '<path fill-rule="evenodd" clip-rule="evenodd" d="M40.6344 1.54054C42.7224 1.54054 44.415 3.20534 44.415 5.25913C44.4149 7.31285 42.7224 8.97726 40.6344 8.97726C38.5465 8.97715 36.8544 7.31278 36.8543 5.25913C36.8543 3.20541 38.5465 1.54066 40.6344 1.54054ZM40.6344 2.89515C39.3074 2.89527 38.2315 3.95388 38.2315 5.25913C38.2316 6.56431 39.3075 7.62298 40.6344 7.6231C41.9619 7.6231 43.0377 6.56438 43.0378 5.25913C43.0378 3.95381 41.9615 2.89515 40.6344 2.89515Z" fill="#6A2B3A"/>' +
      '<path d="M54.5383 1.5C54.5824 1.50002 54.623 1.52308 54.6457 1.56012L56.3874 4.32219C56.436 4.40214 56.5536 4.40209 56.6023 4.32219L58.3439 1.56012C58.3667 1.52307 58.4077 1.5 58.4518 1.5H59.8406C59.9387 1.5 59.9986 1.60666 59.9466 1.68857L57.2631 5.90045C57.2591 5.90675 57.2558 5.91414 57.2543 5.92141V8.76501C57.2542 8.87703 57.1616 8.96811 57.0477 8.96815H55.959C55.8451 8.96815 55.7522 8.87706 55.7516 8.76501V5.9665C55.7516 5.94261 55.7399 5.92093 55.727 5.90045L53.0435 1.68857C52.9916 1.60623 53.0515 1.50012 53.1495 1.5H54.5383Z" fill="#6A2B3A"/>' +
      "</svg>";

    function _esc(str) {
      return str == null
        ? ""
        : String(str)
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/"/g, "&quot;")
            .replace(/'/g, "&#39;");
    }

    customElements.define("lpl-record-panel", LplRecordPanel);
  }

  /* ══════════════════════════════════════════════════════════════════════
     Custom Element: lpl-rules-panel（Rules 弹框）
     ══════════════════════════════════════════════════════════════════════ */
  if (!customElements.get("lpl-rules-panel")) {
    class LplRulesPanel extends HTMLElement {
      connectedCallback() {
        if (this._init) return;
        this._init = true;
        _ensureBase();
        this._modal = null;
        this.addEventListener("click", this);
      }
      disconnectedCallback() {
        this.removeEventListener("click", this);
        if (this._modal) {
          this._modal.destroy();
          this._modal = null;
        }
      }
      handleEvent(e) {
        if (e.type !== "click") return;
        if (e.target.closest(".m26-modal-overlay")) return;
        this._open();
      }
      _open() {
        const tpl = this.querySelector("template");
        if (!tpl) return;
        if (!this._modal) this._modal = new _Modal("lpl-modal-box lpl-modal");
        const content = tpl.content.cloneNode(true);
        const wrapper = document.createElement("div");
        wrapper.appendChild(content);
        this._modal.setContent(wrapper.innerHTML);
        this._modal.show();
      }
    }
    customElements.define("lpl-rules-panel", LplRulesPanel);
  }

  /* ══════════════════════════════════════════════════════════════════════
     Custom Element: lpl-login-guard（点击前校验登录，未登录则打开登录框）
     ══════════════════════════════════════════════════════════════════════ */
  if (!customElements.get("lpl-login-guard")) {
    class LplLoginGuard extends HTMLElement {
      connectedCallback() {
        this.addEventListener("click", this, true);
      }
      disconnectedCallback() {
        this.removeEventListener("click", this, true);
      }
      handleEvent(e) {
        if (e.type !== "click") return;
        _ensureBase();
        _syncAuth();
        if (!_auth || !_auth.user) {
          e.stopPropagation();
          e.preventDefault();

          /* 根据被点击按钮的类型，写入对应的自动操作标记
           * 登录成功刷新页面后，lpl-app._checkAutoDrawOnLoad 会读取此标记
           * 自动滚动到组件并执行对应操作（抽奖 or 打开中奖记录）
           */
          try {
            /* 向上找最近的 lpl-app 获取 sectionId */
            const app = this.closest("lpl-app");
            const sectionId = (app && app._sectionId) || "1";

            /* 判断操作类型：
             *   - 内部有 [data-lpl-draw-btn] → 抽奖按钮
             *   - 内部有 .lpl-footer__link--record → 中奖记录按钮
             *   - 默认 → draw
             */
            let action = "draw";
            if (this.querySelector(".lpl-footer__link--record")) {
              action = "record";
            }

            sessionStorage.setItem(
              "lpl_auto_action",
              JSON.stringify({ action, sectionId }),
            );
          } catch (_) {}

          _openLoginForLPL();
        }
      }
    }
    customElements.define("lpl-login-guard", LplLoginGuard);
  }

  //加载产品集合
  const handleEl = document.querySelector("[data-lpl-collection-handle]");
  const jsonEl = document.querySelector("[data-lpl-collection-products-json]");
  const recordPlaceholder = document.querySelector(
    "[data-lpl-record-placeholder]",
  );

  if (!handleEl || !jsonEl) return;

  const handle = handleEl.dataset.handle;

  function buildCollectionProductMap(map, fallbackImg) {
    try {
      /* 校验：确保解析结果是普通对象（而非数组等） */
      if (typeof map !== "object" || Array.isArray(map)) return {};
      /* 遍历每个商品，修复 featured_image 无效的情况 */
      const fb = fallbackImg || (recordPlaceholder ? recordPlaceholder.src : "");
      Object.keys(map).forEach(function (pid) {
        const product = map[pid];
        if (!product || typeof product !== "object") return;
        if (!_isValidImgUrl(product.featured_image)) {
          /* 尝试从 images 数组中取第一张有效图 */
          const firstValid =
            Array.isArray(product.images) &&
            product.images.find(function (img) {
              return _isValidImgUrl(img);
            });
          product.featured_image = firstValid || fb;
        }
      });

      return map;
    } catch (err) {
      /* 打印真实错误，方便排查 JSON 格式问题 */
    }
  }

  // 拉取所有产品（支持分页）
  async function fetchAllProducts(handle) {
    let products = [];
    let page = 1;
    while (true) {
      const res = await fetch(
        `/collections/${handle}/products.json?limit=250&page=${page}`,
      );
      if (!res.ok) break;
      const data = await res.json();
      if (!data.products || !data.products.length) break;
      products = products.concat(data.products);
      if (data.products.length < 250) break;
      page++;
    }
    return products;
  }

  // 转换为你原来 Liquid 输出的格式（以 id 为 key 的对象）
  function buildProductMap(products) {
    const map = {};
    const seen = new Set();
    for (const p of products) {
      if (seen.has(p.id)) continue;
      seen.add(p.id);
      map[p.id] = {
        id: p.id,
        title: p.title,
        handle: p.handle,
        url: `/products/${p.handle}`,
        available: p.variants.some((v) => v.available),
        price: p.variants[0]?.price
          ? Math.round(parseFloat(p.variants[0].price) * 100)
          : 0,
        compare_at_price: p.variants[0]?.compare_at_price
          ? Math.round(parseFloat(p.variants[0].compare_at_price) * 100)
          : null,
        featured_image: p.images[0]?.src ?? null,
        images: p.images.slice(0, 3).map((img) => img.src),
        tags: p.tags,
        type: p.product_type,
        variants: p.variants.map((v) => ({
          id: v.id,
          title: v.title,
          price: Math.round(parseFloat(v.price) * 100),
          available: v.available,
          sku: v.sku,
        })),
      };
    }
    return map;
  }

  try {
    const products = await fetchAllProducts(handle);
    const productMap = buildProductMap(products);
    // 更新 script 标签内容，供其他 JS 读取
    jsonEl.textContent = JSON.stringify(productMap);
    // 触发自定义事件，通知其他模块数据已就绪
    _collectionProductMap=buildCollectionProductMap(productMap,recordPlaceholder)
  } catch (e) {
    console.error("[LPL] 产品数据加载失败", e);
  }
})();
